import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from './cliente';
import { chaves } from './query';
import type {
  ConfigsIntegrador, CustosDiretosProjeto, Despesas, Sazonalidade, Usuario,
} from './tipos';

/* Dados do painel de administração. Leitura da base inteira, mais três
 * escritas: o papel, o e-mail do cadastro de uma empresa e a exclusão dela.
 *
 * Os tipos descrevem o que a API devolve e param aí. Não há modelo de domínio
 * a compartilhar: o painel LÊ o cadastro dos outros, não calcula nada. Quem
 * calcula é o `core`, e o painel não chama o motor de propósito. */

export type ResumoAdmin = {
  contas: number;
  contas_cadastradas: number;
  empresas: number;
  empresas_com_cnpj: number;
  projetos: number;
  orcamentos: number;
  diagnosticos: number;
  acessos_7d: number;
  acessos_30d: number;
  ativos_7d: number;
};

export type EmpresaAdmin = {
  id: string;
  nome_empresa: string;
  cnpj: string | null;
  email: string;
  regime_tributario: string | null;
  criado_em: string;
  atualizado_em: string;
  usuario_id: string;
  usuario_email: string | null;
  anonimo: boolean;
  papel: 'dono' | 'admin';
  ultimo_acesso: string | null;
  projetos: number;
  orcamentos: number;
  acessos: number;
};

/** A empresa do detalhe: o que aparece na lista MAIS tudo que ela configurou. */
export type CadastroAdmin = EmpresaAdmin & {
  whatsapp: string | null;
  /** Meta de margem líquida, em PONTOS percentuais (foi o usuário quem digitou). */
  lucro_alvo: number | null;
  projetos_mes: number | null;
  despesas: Despesas | null;
  configs: ConfigsIntegrador | null;
  sazonalidade: Sazonalidade | null;
};

export type ProjetoAdmin = {
  id: string;
  nome_projeto: string;
  quantidade_modulos: number | null;
  kwp_sistema: number | null;
  custo_kit: number | null;
  valor_venda: number | null;
  custos_diretos: CustosDiretosProjeto | null;
  atualizado_em: string;
};

/** Uma análise arquivada, salva no histórico ou gerada como laudo. */
export type ArquivoAdmin = { id: string; projeto_id: string | null; criado_em: string };

export type DetalheEmpresa = {
  cadastro: CadastroAdmin;
  projetos: ProjetoAdmin[];
  orcamentos: ArquivoAdmin[];
  diagnosticos: ArquivoAdmin[];
  acessos: { criado_em: string }[];
};

export type DiaDeAcesso = { dia: string; visitas: number; pessoas: number };

export type Administrador = {
  id: string;
  email: string | null;
  papel: 'dono' | 'admin';
  ultimo_acesso: string | null;
  criado_em: string;
};

export function useResumoAdmin() {
  return useQuery({
    queryKey: chaves.adminResumo,
    queryFn: () => api.obter<ResumoAdmin>('/admin/resumo'),
  });
}

export function useEmpresasAdmin(busca: string, pagina: number) {
  return useQuery({
    queryKey: chaves.adminEmpresas(busca, pagina),
    queryFn: () =>
      api.obter<{ itens: EmpresaAdmin[]; total: number }>('/admin/empresas', {
        parametros: { busca: busca || undefined, pagina },
      }),
    // Some com o pisca-pisca ao trocar de página ou digitar na busca.
    placeholderData: (anterior) => anterior,
  });
}

export function useEmpresaAdmin(id: string | null) {
  return useQuery({
    queryKey: chaves.adminEmpresa(id ?? ''),
    queryFn: () => api.obter<DetalheEmpresa>(`/admin/empresas/${id}`),
    enabled: id !== null,
  });
}

export function useAcessosAdmin(dias: number) {
  return useQuery({
    queryKey: chaves.adminAcessos(dias),
    queryFn: () => api.obter<{ dias: number; serie: DiaDeAcesso[] }>('/admin/acessos', {
      parametros: { dias },
    }),
  });
}

export function useAdministradores() {
  return useQuery({
    queryKey: chaves.adminAdministradores,
    queryFn: () => api.obter<Administrador[]>('/admin/administradores'),
  });
}

export function useDefinirPapel() {
  const clienteQuery = useQueryClient();
  return useMutation({
    mutationFn: (dados: { email: string; papel: 'dono' | 'admin' }) =>
      api.ajustar<Usuario>('/admin/administradores', dados),
    onSuccess: () => {
      void clienteQuery.invalidateQueries({ queryKey: ['admin'] });
    },
  });
}

/**
 * O e-mail do CADASTRO de uma empresa. Não é o de login: aquele é credencial e
 * o painel não a toca. Este é a chave que permite ao dono reivindicar o CNPJ,
 * e existe aqui porque, digitado errado, ninguém mais consegue corrigi-lo.
 */
export function useAlterarEmailDaEmpresa(id: string) {
  const clienteQuery = useQueryClient();
  return useMutation({
    mutationFn: (email: string) =>
      api.ajustar<{ id: string; email: string }>(`/admin/empresas/${id}`, { email }),
    onSuccess: () => {
      void clienteQuery.invalidateQueries({ queryKey: ['admin'] });
    },
  });
}

/** Apagar a empresa e a conta dela. Exige a senha de quem está apagando. */
export function useExcluirEmpresa(id: string) {
  const clienteQuery = useQueryClient();
  return useMutation({
    mutationFn: (senha: string) => api.remover<void>(`/admin/empresas/${id}`, { corpo: { senha } }),
    onSuccess: () => {
      void clienteQuery.invalidateQueries({ queryKey: ['admin'] });
    },
  });
}
